import { create } from 'zustand';

import type { FilterGroup, GroupLogic } from '../_shared/flow/filter';

/**
 * Launcher V2 — the play-flow's shared panel state: which waterfall rung (or
 * the Audience Rule) the persistent `MembersPanel` is scoped to, plus the
 * Audience Rule's staged filter draft.
 *
 * A2 U5 introduced this as a LABEL-ONLY store — the rows below the label never
 * changed, because real matched-member evaluation was PR B. As of PR B U3 (R4)
 * the scope is load-bearing: `MembersPanel` runs U2's matched-member query for
 * whatever this store points at and renders the real matched subset.
 *
 * Module-scope zustand store (root CLAUDE.md "Zustand for Module-Scope Client
 * State" convention), not component state or props-drilling, for two reasons:
 *
 *  1. The scope must survive `QualifyStep` unmounting when the stepper's Glaze
 *     `TabPanel` swaps away from Qualify (base-ui `Tabs` unmount inactive
 *     panels by default — see `node_modules/@clay-run/glaze/src/tabs.tsx`).
 *     As of PR B follow-up U5, `MembersPanel` itself is ALSO rendered inside
 *     each step's `TabPanel` (via a `membersPanel` slot `PlayFlowShell`
 *     builds once and hands to all three steps) and so unmounts/remounts on
 *     every tab switch too — the scope has to survive the PANEL unmounting,
 *     not just the step. A module-scope store outlives both.
 *  2. `MembersPanel` and `QualifyStep`'s `AudienceRuleCard` are SIBLINGS, and
 *     both need the SAME Audience Rule draft: the card edits it, the panel
 *     previews it. `useDraftState` could not do that job here — it is
 *     component-local `useState` under the hood (`web/app-home/src/hooks/
 *     useDraftState.ts`), so calling it with the same `entityKey` in two
 *     components yields two INDEPENDENT drafts that silently diverge. Holding
 *     the draft here is what makes "the panel previews exactly what the card
 *     shows" true structurally. The SSE-churn hazard `useDraftState` exists to
 *     guard against does not apply to a store: the draft lives outside React,
 *     so no parent re-render can reseed or wipe it.
 *
 * `PlayFlowShell` resets this store to {@link initialState} on mount (and
 * unmount), keyed on `playId`, so neither a scope nor a staged Audience Rule
 * draft from one Play ever leaks into another Play opened next in the same
 * browser session.
 */

export type RungScope =
  | { kind: 'audience' }
  | {
      kind: 'rung';
      /**
       * The scoped rung's stable `PlaybookRung.id`.
       *
       * Keyed by the stable rung id, so the scope survives a reorder
       * structurally: a reorder rewrites every `position` and nothing else, and
       * a `segmentationId` can legitimately repeat across rungs (the seed's
       * catch-all borrows an earlier rung's). `id` is the only field that
       * addresses exactly one rung and never moves — so the label's frozen
       * `rungName` and the matched-member lookup can never come apart and name
       * one rung while showing another's members.
       */
      rungId: string;
      rungName: string;
      /**
       * Which Playbook the rung belongs to. Carried on the scope because
       * `MembersPanel` is a sibling of the waterfall, not a descendant: it has
       * no other way to reach the id, and re-deriving it would mean a second
       * `getPlays`/`getPlaybook` round trip for data `QualifyStep` already
       * holds. It is the ladder query's cache key, so the panel and the Review
       * step share one fetch.
       */
      playbookId: string;
    }
  | {
      kind: 'rung-draft';
      /** Same stable rung id as the `'rung'` kind — see that field's doc. */
      rungId: string;
      rungName: string;
      /** Same reasoning as the `'rung'` kind's `playbookId` — see that field's doc. */
      playbookId: string;
      /**
       * The rung's UNSAVED staged filter conditions (U6, R-Q1 — live filter
       * preview). A distinct `kind` rather than an optional field on `'rung'`
       * (Decision 7): a saved rung's Preview reads the ladder's already-computed
       * claim for this rung's id; a rung-draft instead runs a fresh
       * `matchMembersByFilter` against the whole campaign for the STAGED
       * conditions — the two are different queries (one is a lookup, the other
       * a live match), not the same query with an extra parameter.
       *
       * W2 (fix-round2-plan.md) — despite running a different query, a
       * rung-draft's PREVIEW is still ladder-aware: `matchedIdsForRungDraftScope`
       * (`members-panel-scope.ts`) reduces this raw match by every ladder rung
       * ABOVE this rung's position before either the inline count or
       * `MembersPanel` render it, so the previewed bucket always agrees with
       * what Save would actually produce.
       *
       * Round-3 (U25, Decision 9, orchestrator override 2026-09-12) — the
       * preview is honest-null (em-dash, plus `RUNG_DRAFT_SHARED_SEGMENTATION_
       * NOTE` in `MembersPanel`) whenever this rung's `segmentationId` is
       * shared with a rung ABOVE it, because `SegmentationConditionsEditor`
       * saves conditions onto the segmentation IDENTITY, not the rung — saving
       * here would also move that higher-priority rung's claim, so the ladder
       * above this position can no longer be assumed fixed. An UNSHARED
       * catch-all (or any rung a LOWER rung borrows from, including the
       * always-last catch-all) still previews as its own ladder entry — a
       * sharer below cannot retroactively change a bucket already computed
       * above it.
       */
      groups: FilterGroup[];
      groupLogic: GroupLogic;
    }
  | null;

/** The Audience Rule's staged, UNSAVED filter (persisting it is Phase 1). */
export interface AudienceRuleDraft {
  enabled: boolean;
  groups: FilterGroup[];
  groupLogic: GroupLogic;
}

export const DEFAULT_AUDIENCE_DRAFT: AudienceRuleDraft = {
  enabled: false,
  groups: [],
  groupLogic: 'AND',
};

interface RungScopeState {
  scope: RungScope;
  audienceDraft: AudienceRuleDraft;
}

/** Exported per the store convention — reset idiom is `useRungScopeStore.setState(initialState, true)`. */
export const initialState: RungScopeState = {
  scope: null,
  audienceDraft: DEFAULT_AUDIENCE_DRAFT,
};

export const useRungScopeStore = create<RungScopeState>(() => ({ ...initialState }));

export function setRungScope(scope: RungScope): void {
  useRungScopeStore.setState({ scope });
}

/**
 * Resets the WHOLE panel state — the scope and the staged Audience Rule draft.
 * Both belong to the Play they were set on (see the module doc's `PlayFlowShell`
 * note); carrying either into the next Play would show one Play's preview under
 * another Play's name.
 */
export function clearRungScope(): void {
  useRungScopeStore.setState({ ...initialState });
}

/** Shallow-merges an edit into the Audience Rule draft (same ergonomics as `useDraftState`'s `setDraft`). */
export function setAudienceDraft(patch: Partial<AudienceRuleDraft>): void {
  useRungScopeStore.setState((state) => ({
    audienceDraft: { ...state.audienceDraft, ...patch },
  }));
}

export function selectRungScope(state: RungScopeState): RungScope {
  return state.scope;
}

export function selectAudienceDraft(state: RungScopeState): AudienceRuleDraft {
  return state.audienceDraft;
}

/**
 * QA round 7 (item 11) — Spencer: the first-match-wins explanation is noise
 * on every render; the caption now says only what is on screen and, for a
 * draft, that it is unsaved. The empty-state line below the caption
 * (`MembersPanel.tsx:591-595`, "No campaign members match this selection.")
 * is unchanged and is the only sentence a zero-match preview adds.
 */
export function rungScopeLabel(scope: RungScope): string {
  if (scope === null) {
    return 'Showing all campaign members — no rung selected yet.';
  }
  if (scope.kind === 'audience') {
    return 'Showing the members matching the Audience Rule as currently staged.';
  }
  if (scope.kind === 'rung-draft') {
    return `Previewing "${scope.rungName}" with unsaved conditions.`;
  }
  return `Showing members routed to "${scope.rungName}".`;
}
